/**
 * Reports response PROJECTIONS — computed aggregates, no atoms.
 *
 * `GET /reports/business` and `GET /reports/claire`. Counts only: the reports
 * deliberately carry no money figures. Rates are 0–1 fractions and are null
 * when there was nothing to divide by.
 */
import { leadSourceValues } from '@borradh-workspace/labels';
import { z } from 'zod';
const reportRangeSchema = z.object({
    from: z.string(),
    to: z.string(),
    previousFrom: z.string(),
    previousTo: z.string(),
    timeZone: z.string(),
});
const comparisonSchema = z.object({ value: z.number(), previous: z.number() });
const rateComparisonSchema = z.object({
    value: z.number().nullable(),
    previous: z.number().nullable(),
});
export const appointmentReportSourceSchema = z.enum([
    'booking_page',
    'claire_chat',
    'claire_phone',
    'team',
    'calendar_sync',
]);
export const paymentReportMethodSchema = z.enum([
    'card_terminal',
    'saved_card',
    'qr_self_checkout',
    'manual_card',
    'cash',
    'gift_card',
    'online_deposit',
]);
const namedQuantitySchema = z.object({
    name: z.string(),
    quantity: z.number(),
});
/** `GET /reports/business` */
export const businessReportSchema = z.object({
    range: reportRangeSchema,
    headline: z.object({
        appointments: comparisonSchema,
        clientsSeen: comparisonSchema,
        newClients: comparisonSchema,
        noShowRate: rateComparisonSchema,
    }),
    appointments: z.object({
        total: z.number(),
        attended: z.number(),
        notCheckedOut: z.number(),
        upcoming: z.number(),
        cancelled: z.number(),
        noShows: z.number(),
        withPaidDeposit: z.number(),
        averageLeadDays: z.number().nullable(),
        bySource: z.array(z.object({ source: appointmentReportSourceSchema, count: z.number() })),
        byDay: z.array(z.object({ date: z.string(), byClients: z.number(), byTeam: z.number() })),
        byTeamMember: z.array(z.object({
            userId: z.string(),
            name: z.string(),
            total: z.number(),
            attended: z.number(),
            noShows: z.number(),
        })),
    }),
    sales: z.object({
        sales: z.number(),
        open: z.number(),
        refunded: z.number(),
        treatmentsSold: z.number(),
        productsSold: z.number(),
        busiestWeekday: z.number().nullable(),
        topTreatments: z.array(namedQuantitySchema),
        topProducts: z.array(namedQuantitySchema),
        byTeamMember: z.array(namedQuantitySchema),
    }),
    clients: z.object({
        seen: z.number(),
        new: z.number(),
        returning: z.number(),
        bookedAgain: z.number(),
        bookedAgainOutOf: z.number(),
        newBySource: z.array(z.object({
            source: z.enum(leadSourceValues).nullable(),
            count: z.number(),
        })),
    }),
    payments: z.object({
        total: z.number(),
        byMethod: z.array(z.object({ method: paymentReportMethodSchema, count: z.number() })),
        refunds: z.number(),
        salesWithTip: z.number(),
        depositsPaid: z.number(),
        depositsKept: z.number(),
    }),
});
const replySpeedSchema = z.object({
    replies: z.number(),
    medianSeconds: z.number().nullable(),
    averageSeconds: z.number().nullable(),
    overAnHour: z.number(),
});
export const conversationChannelSchema = z.enum([
    'whatsapp',
    'instagram_dm',
    'facebook_messenger',
    'sms',
]);
/** `GET /reports/claire` */
export const claireReportSchema = z.object({
    range: reportRangeSchema,
    headline: z.object({
        conversations: comparisonSchema,
        handledByClaire: comparisonSchema,
        bookings: comparisonSchema,
        newContacts: comparisonSchema,
    }),
    conversations: z.object({
        total: z.number(),
        claireOnly: z.number(),
        claireThenTeam: z.number(),
        teamOnly: z.number(),
        unanswered: z.number(),
        waitingNow: z.number(),
        claireOutreach: z.number(),
        messages: z.object({
            fromClients: z.number(),
            fromClaire: z.number(),
            fromTeam: z.number(),
        }),
        averageMessages: z.number().nullable(),
        averageMessagesClaireOnly: z.number().nullable(),
        averageMessagesWithTeam: z.number().nullable(),
    }),
    replySpeed: z.object({
        claire: replySpeedSchema,
        team: replySpeedSchema,
        firstReplyMedianSeconds: z.object({
            claire: z.number().nullable(),
            team: z.number().nullable(),
        }),
        unanswered: z.number(),
    }),
    outcomes: z.object({
        bookings: z.number(),
        bookingsByChannel: z.object({ chat: z.number(), phone: z.number() }),
        depositsSent: z.number(),
        depositsPaid: z.number(),
        chatsThatBooked: z.number(),
        newContacts: z.number(),
    }),
    byDay: z.array(z.object({
        date: z.string(),
        fromClients: z.number(),
        fromClaire: z.number(),
        fromTeam: z.number(),
    })),
    byChannel: z.array(z.object({
        channel: conversationChannelSchema,
        conversations: z.number(),
        fromClaire: z.number(),
        fromTeam: z.number(),
        medianFirstReplySeconds: z.number().nullable(),
    })),
    busyTimes: z.array(z.object({ weekday: z.number(), block: z.number(), messages: z.number() })),
});
/** PostHog key gating the Reports page, its API and Claire's report tools. */
export const REPORTS_ROLLOUT_FLAG = 'rollout-reports';
/** `GET /reports/availability` — whether this org has the Reports page. */
export const reportsAvailabilitySchema = z.object({ enabled: z.boolean() });
//# sourceMappingURL=reports.js.map